import type { LinhaAtivacao } from '@/hooks/useAtivacaoLinhas';
import { linhaEstaValidada, situacaoLinhaEsim } from '../../whatsapp-inbox/ativacaoResumo';

const AVISOS: Record<string, string> = {
  confirmacao: 'perguntamos se funciona', reteste_simcard: 'pedimos novo teste',
  compra_simcard: 'pedimos a foto do chip', chip_recebido: 'avisamos que o chip chegou', solicitacao_enviada: 'avisamos que foi à Claro',
  estimativa: 'avisamos o prazo', ajuda_simcard: 'orientamos o novo teste',
  escolha_ajuda_real: 'perguntamos eSIM ou chip', orientacao_esim_real: 'mandamos o passo a passo',
};

/** O que o cliente recebeu por último — a prova de que a automação falou com ele. */
export function ultimoAvisoEsim(l: { aviso_finalidade?: string | null; aviso_status?: string | null; aviso_em?: string | null; aviso_rotulo?: string | null }) {
  if (l.aviso_rotulo) return l.aviso_rotulo;
  if (!l.aviso_finalidade || !l.aviso_em) return 'Nenhum aviso enviado ao cliente';
  const quando = new Date(l.aviso_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const rotulo = AVISOS[l.aviso_finalidade] || l.aviso_finalidade;
  return l.aviso_status === 'incerta' ? `Envio não confirmado: ${rotulo} · ${quando}` : `Cliente: ${rotulo} · ${quando}`;
}

/**
 * O ÚLTIMO CONTATO, em coluna própria (Douglas 22/09/2026): "está amontoando
 * muita informação". Sem o prefixo "Cliente:", que na coluna vira ruído — o
 * cabeçalho já diz o que é. Envio incerto continua se identificando.
 */
export function avisoCurtoEsim(l: { aviso_finalidade?: string | null; aviso_status?: string | null; aviso_em?: string | null; aviso_rotulo?: string | null }) {
  const texto = (l.aviso_rotulo || '').replace(/^Cliente: /, '');
  if (!l.aviso_finalidade || !l.aviso_em) return { texto: 'Nada enviado ainda', incerto: false, vazio: true };
  return { texto: texto || 'Aviso enviado', incerto: l.aviso_status === 'incerta', vazio: false };
}

export function statusTriagemEsim(l: LinhaAtivacao & { troca_status: string | null; previsao_em: string | null; retorno_em?: string | null; situacao_rotulo?: string | null }, agora = Date.now()) {
  // O rótulo vem do backend (domain/qualidade/situacao-esim.ts), fonte única.
  // O cálculo local fica como rede: tela velha contra API nova não quebra.
  if (l.situacao_rotulo) return l.situacao_rotulo;
  if (l.retorno_em && new Date(l.retorno_em).getTime() > agora && !linhaEstaValidada(l)) {
    const quando = new Date(l.retorno_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    return `Retorno marcado para ${quando} · ${situacaoLinhaEsim(l, agora)}`;
  }
  return situacaoLinhaEsim(l, agora);
}

/**
 * OS ESTADOS SÃO OS DA TRIAGEM — não se inventa status novo (Douglas
 * 22/09/2026). São os mesmos quatro que pintam a triagem eSIM desde sempre;
 * saíram de dentro do TriagemEsim.tsx para a Conferência usar os mesmos, e
 * nada além deles. O nome de cada situação vem de situacao-esim.ts, no
 * backend: a fonte única continua sendo uma só.
 */
export type EstadoEsim = 'validado' | 'andamento' | 'espera' | 'parado';
// ⛔ O QUE NÃO ANDA É PARADO, mesmo que pareça em andamento (Douglas 23/09/2026,
// auditoria). Envio que nunca confirmou e problema técnico de conexão eram pintados
// como "Em andamento" e SUMIAM do filtro Parado da Produtividade — justamente as
// linhas que precisam de gente. Sem validar não há comissão: elas têm que doer.
const PARADO = new Set(['classificar', 'esim_iniciar', 'pediu_ajuda', 'revisar_resposta',
  'troca_pendencia', 'troca_incerta', 'troca_pronta', 'troca_prazo_vencido', 'chip_foto_recebida',
  'envio_nao_confirmado', 'sem_conversa', 'conexao_caida', 'conversa_de_outro']);

export interface LinhaEstadoEsim {
  situacao?: string | null; confirmacao_status?: string | null;
  validada_em?: string | null; revisao?: number; revisao_validada?: number | null;
  aguardando_chip_por?: string | null; retorno_em?: string | null;
}
export function estadoEsim(l: LinhaEstadoEsim): EstadoEsim {
  if (l.situacao === 'validado' || linhaEstaValidada(l as never)) return 'validado';
  // ⛔ AJUDA ABERTA VENCE O AGENDAMENTO — a mesma exceção que o backend aplica
  // em situacaoComRetorno (Douglas 22/09/2026): "o cliente está esperando
  // AGORA". O front não conhecia a exceção e mandava para 'Fora da vez' quem
  // tinha digitado 1, escondendo o caso mais urgente da fila.
  if (l.situacao === 'pediu_ajuda') return 'parado';
  if (l.aguardando_chip_por || l.retorno_em) return 'espera';
  if (l.situacao) return PARADO.has(l.situacao) ? 'parado' : 'andamento';
  return l.confirmacao_status ? 'andamento' : 'parado';
}
// 'Fora da vez' e não 'Esperando': esperar a Claro devolver a troca é
// andamento, e chamar isto de espera fazia pensar que era o e-mail parado com
// a gerente (Douglas 22/09/2026). Aqui é só quem saiu da fila no botão —
// retorno agendado ou cliente que ainda não tem o chip.
export const ROTULO_ESTADO_ESIM: Record<EstadoEsim, string> = {
  parado: 'Parado', andamento: 'Em andamento', espera: 'Fora da vez', validado: 'Validado',
};
export const COR_ESTADO_ESIM: Record<EstadoEsim, string> = {
  validado: 'text-[#075E54] dark:text-[#25D366]', andamento: 'text-primary',
  espera: 'text-primary', parado: 'text-destructive',
};
export const FUNDO_ESTADO_ESIM: Record<EstadoEsim, string> = {
  validado: 'bg-[#075E54]', andamento: 'bg-primary',
  espera: 'bg-primary/40', parado: 'bg-destructive',
};
